import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import Icon from '../../Icon';
import { formatDateWithTime } from '../../../utils/format';

function AdminSMSHistoryView() {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);

  const [selectedDate, setSelectedDate] = useState('');

  const [messages, setMessages] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.get('/auth/users');
        const list = Array.isArray(res?.data?.users) ? res.data.users : [];
        setUsers(list);
      } catch (e) {
        console.error('Failed to fetch users for SMS history', e);
      }
    };
    fetchUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = (u.name || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      const phone = (u.phone || '').toLowerCase();
      const idNumber = (u.idNumber || '').toLowerCase();
      return (
        name.includes(q) ||
        email.includes(q) ||
        phone.includes(q) ||
        idNumber.includes(q)
      );
    });
  }, [userSearch, users]);

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  const kindLabel = (kind) => {
    if (kind === 'manual_admin') return 'Manual';
    if (kind === 'rent_reminder') return 'Reminder';
    if (kind === 'pin_reset') return 'PIN Reset';
    if (kind === 'chamaa_payout') return 'Chamaa Payout';
    if (kind === 'welfare_payout') return 'Welfare Payout';
    if (kind === 'frp_disbursement') return 'FRP Disbursement';
    if (kind === 'admin_topup') return 'Admin Top Up';
    return 'Other';
  };

  const kindColor = (kind) => {
    if (kind === 'manual_admin') return 'bg-violet-500/20 text-violet-300 border-violet-500/30';
    if (kind === 'rent_reminder') return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
    if (kind === 'pin_reset') return 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30';
    if (kind === 'chamaa_payout') return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30';
    if (kind === 'welfare_payout') return 'bg-rose-500/20 text-rose-300 border-rose-500/30';
    if (kind === 'frp_disbursement') return 'bg-sky-500/20 text-sky-300 border-sky-500/30';
    if (kind === 'admin_topup') return 'bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/30';
    return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
  };

  const resetAndFetch = () => {
    setPage(1);
    fetchMessages(1, selectedUser?._id, selectedDate);
  };

  const fetchMessages = async (pageArg, userIdArg, dateArg) => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      params.append('page', String(pageArg || 1));
      params.append('limit', String(limit));
      if (userIdArg) params.append('userId', userIdArg);
      if (dateArg) params.append('date', dateArg);
      const res = await api.get(`/frp/admin/sms-history?${params.toString()}`);
      const data = res?.data || {};
      setMessages(Array.isArray(data.items) ? data.items : []);
      setTotalPages(data.totalPages || 1);
      setTotalItems(data.total || 0);
    } catch (e) {
      console.error('Failed to load SMS history', e);
      setError(e?.response?.data?.error || 'Failed to load SMS history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages(page, selectedUser?._id, selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSelectUser = (u) => {
    setSelectedUser(u);
    setUserSearch('');
    setUserDropdownOpen(false);
    setPage(1);
    fetchMessages(1, u?._id, selectedDate);
  };

  const clearFilters = () => {
    setSelectedUser(null);
    setSelectedDate('');
    setUserSearch('');
    setPage(1);
    fetchMessages(1, null, '');
  };

  const handleDateChange = (value) => {
    setSelectedDate(value);
    setPage(1);
    fetchMessages(1, selectedUser?._id, value);
  };

  const handlePageChange = (direction) => {
    if (direction === 'prev' && page > 1) {
      setPage((p) => p - 1);
    } else if (direction === 'next' && page < totalPages) {
      setPage((p) => p + 1);
    }
  };

  return (
    <div className="min-h-screen font-sans pb-8 relative overflow-hidden" style={{
      background: 'linear-gradient(135deg, #1a1625 0%, #2d1f47 50%, #3d2563 100%)'
    }}>
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-40 left-10 w-80 h-80 bg-violet-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '5s' }} />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-fuchsia-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '7s', animationDelay: '2s' }} />
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)`,
          backgroundSize: '30px 30px'
        }} />
      </div>

      {/* Header */}
      <div className="relative z-10 px-4 sm:px-6 lg:px-8 pt-6 pb-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/admin')}
              className="group flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all text-white/70 hover:text-white"
            >
              <svg className="w-4 h-4 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm font-medium">Dashboard</span>
            </button>
            <button
              onClick={() => navigate('/admin/sms')}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-violet-500/20 hover:bg-violet-500/30 border border-violet-500/30 hover:border-violet-500/50 transition-all text-violet-300 hover:text-white"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span className="text-sm font-medium">Send SMS</span>
            </button>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 shadow-2xl shadow-violet-500/30 flex items-center justify-center">
              <svg className="w-8 h-8 sm:w-10 sm:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                SMS History
              </h1>
              <p className="text-purple-200/60 text-sm sm:text-base mt-1">
                View and search all sent messages
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Filters Card */}
        <div className="rounded-2xl mb-6 p-5 sm:p-6" style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)',
          backdropFilter: 'blur(40px)',
          border: '1px solid rgba(255,255,255,0.1)'
        }}>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* User Search */}
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-purple-200/70 mb-2 uppercase tracking-wider">
                Filter by User
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setUserDropdownOpen((o) => !o)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all text-left ${
                    userDropdownOpen 
                      ? 'bg-white/10 border-violet-500/50 ring-2 ring-violet-500/20' 
                      : 'bg-white/5 hover:bg-white/10 border-white/10 hover:border-white/20'
                  } border`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {selectedUser ? (
                      <>
                        <div className="w-9 h-9 bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-xl flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                          {getInitials(selectedUser.name)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-white truncate">{selectedUser.name}</p>
                          <p className="text-xs text-purple-300/50 truncate">{selectedUser.email}</p>
                        </div>
                      </>
                    ) : (
                      <span className="text-sm text-purple-300/50">All users</span>
                    )}
                  </div>
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all ${userDropdownOpen ? 'bg-violet-500/20 rotate-180' : 'bg-white/5'}`}>
                    <svg className="w-4 h-4 text-purple-300/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>

                {userDropdownOpen && (
                  <div className="absolute z-50 w-full mt-2 rounded-xl overflow-hidden" style={{
                    background: 'linear-gradient(180deg, rgba(45,31,71,0.98) 0%, rgba(26,22,37,0.98) 100%)',
                    backdropFilter: 'blur(40px)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)'
                  }}>
                    <div className="p-3 border-b border-white/10">
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="w-4 h-4 text-purple-300/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                        <input
                          type="text"
                          placeholder="Search users..."
                          value={userSearch}
                          onChange={(e) => setUserSearch(e.target.value)}
                          className="w-full pl-10 pr-4 py-2.5 text-sm bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/50 text-white placeholder-purple-300/40"
                          autoFocus
                        />
                      </div>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {filteredUsers.length === 0 ? (
                        <div className="p-6 text-center text-sm text-purple-300/50">No users found</div>
                      ) : (
                        filteredUsers.slice(0, 80).map((u) => (
                          <button
                            key={u._id}
                            type="button"
                            onClick={() => handleSelectUser(u)}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-all text-left group"
                          >
                            <div className="w-8 h-8 bg-gradient-to-br from-purple-700 to-purple-900 rounded-lg flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 group-hover:from-violet-500 group-hover:to-fuchsia-500 transition-all">
                              {getInitials(u.name)}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-white truncate group-hover:text-violet-300 transition-colors">{u.name}</p>
                              <p className="text-xs text-purple-300/50 truncate">{u.email}</p>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Date Filter */}
            <div>
              <label className="block text-xs font-semibold text-purple-200/70 mb-2 uppercase tracking-wider">
                Date
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => handleDateChange(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/50 text-white transition-all"
                style={{ colorScheme: 'dark' }}
              />
            </div>

            {/* Actions */}
            <div className="flex items-end gap-2">
              <button
                type="button"
                onClick={resetAndFetch}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white rounded-xl hover:opacity-90 text-sm font-medium shadow-lg shadow-violet-500/20 transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Refresh</span>
              </button>
              <button
                type="button"
                onClick={clearFilters}
                className="px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-purple-200 rounded-xl text-sm font-medium transition-all"
              >
                Clear
              </button>
            </div>
          </div>

          {/* Filter Summary */}
          <div className="mt-4 pt-4 border-t border-white/5 flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs text-purple-300/60">
              {selectedUser ? (
                <span>
                  Showing messages for <span className="font-semibold text-violet-300">{selectedUser.name}</span>
                  {selectedDate && <> on <span className="font-semibold text-violet-300">{selectedDate}</span></>}
                </span>
              ) : selectedDate ? (
                <span>Showing messages for <span className="font-semibold text-violet-300">{selectedDate}</span></span>
              ) : (
                <span>Showing all messages</span>
              )}
              <span className="ml-2 px-2 py-0.5 rounded-full bg-white/5 text-purple-300/80 font-medium">
                {totalItems.toLocaleString()} total
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-xl bg-rose-500/10 border border-rose-500/20 px-4 py-3 text-sm text-rose-300 flex items-center gap-2">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}

        {/* Messages Table */}
        <div className="rounded-2xl overflow-hidden" style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)',
          backdropFilter: 'blur(40px)',
          border: '1px solid rgba(255,255,255,0.08)'
        }}>
          {loading ? (
            <div className="py-20 text-center">
              <div className="w-10 h-10 border-2 border-violet-500/30 border-t-violet-500 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-purple-300/60 text-sm">Loading messages...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="py-20 text-center">
              <div className="w-16 h-16 rounded-2xl bg-purple-900/30 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-purple-400/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <p className="text-purple-300/60 text-sm">No messages found</p>
              <p className="text-purple-300/40 text-xs mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-purple-200/70 uppercase tracking-wider">Date</th>
                    <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-purple-200/70 uppercase tracking-wider">Recipient</th>
                    <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-purple-200/70 uppercase tracking-wider">Phone</th>
                    <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-purple-200/70 uppercase tracking-wider">Type</th>
                    <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-purple-200/70 uppercase tracking-wider">Message</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {messages.map((m, idx) => (
                    <tr 
                      key={m._id} 
                      className="hover:bg-white/[0.02] transition-colors"
                      style={{ animation: `fadeIn 0.3s ease-out ${idx * 0.03}s both` }}
                    >
                      <td className="px-4 sm:px-6 py-4 align-top">
                        <span className="text-sm text-white/80 whitespace-nowrap">{formatDateWithTime(m.createdAt)}</span>
                      </td>
                      <td className="px-4 sm:px-6 py-4 align-top">
                        {m.user ? (
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="hidden sm:flex w-8 h-8 bg-gradient-to-br from-violet-500/80 to-fuchsia-500/80 rounded-lg items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                              {getInitials(m.user.name)}
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-white truncate">{m.user.name}</div>
                              <div className="text-xs text-purple-300/50 truncate">{m.user.email}</div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-purple-300/50">Unknown</span>
                        )}
                      </td>
                      <td className="px-4 sm:px-6 py-4 align-top">
                        <div className="text-sm text-fuchsia-300 font-mono">{m.to}</div>
                        {m.originalTo && m.originalTo !== m.to && (
                          <div className="text-xs text-purple-300/40 mt-0.5">was: {m.originalTo}</div>
                        )}
                      </td>
                      <td className="px-4 sm:px-6 py-4 align-top">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border ${kindColor(m.kind)}`}>
                          {kindLabel(m.kind)}
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-4 align-top">
                        <div className="text-sm text-white/70 break-words max-w-xs sm:max-w-md leading-relaxed">{m.text}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {messages.length > 0 && (
            <div className="px-4 sm:px-6 py-4 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-xs text-purple-300/60">
                Showing <span className="font-semibold text-white/80">{messages.length}</span> of{' '}
                <span className="font-semibold text-white/80">{totalItems.toLocaleString()}</span> messages
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handlePageChange('prev')}
                  disabled={page <= 1 || loading}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <div className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10">
                  <span className="text-sm font-semibold text-white">{page}</span>
                  <span className="text-sm text-purple-300/50">/</span>
                  <span className="text-sm text-purple-300/70">{totalPages || 1}</span>
                </div>
                <button
                  type="button"
                  onClick={() => handlePageChange('next')}
                  disabled={page >= totalPages || loading}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Custom Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(5px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Custom scrollbar */
        .max-h-64::-webkit-scrollbar {
          width: 6px;
        }
        .max-h-64::-webkit-scrollbar-track {
          background: rgba(255,255,255,0.05);
          border-radius: 3px;
        }
        .max-h-64::-webkit-scrollbar-thumb {
          background: rgba(139,92,246,0.3);
          border-radius: 3px;
        }
        .max-h-64::-webkit-scrollbar-thumb:hover {
          background: rgba(139,92,246,0.5);
        }
        
        /* Date input styling */
        input[type="date"]::-webkit-calendar-picker-indicator {
          filter: invert(0.7);
          cursor: pointer;
        }
      `}</style>
    </div>
  );
}

export default AdminSMSHistoryView;
